import React, {FC, useCallback, useEffect, useState} from 'react'
import {ToastContainer} from 'react-toastify'
import {PageTitle} from '../../../../_metronic/layout/core'
import {ConfirmModal} from '../../../../shared/components/ConfirmModal'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import {createQuestion, deleteQuestion, loadQuestions, updateQuestion} from './Api'
import {IQuestionModel} from './QuestionModel'
import {QuestionTable} from './QuestionTable'
import {QuestionFormModal} from './QuestionFormModal'

export const HR_QUESTION_PATH = '/hr/question'

const QuestionPage: FC = () => {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [questions, setQuestions] = useState<IQuestionModel[]>([])
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [showForm, setShowForm] = useState(false)
  const [editingItem, setEditingItem] = useState<IQuestionModel | null>(null)
  const [deletingItem, setDeletingItem] = useState<IQuestionModel | null>(null)

  const fetchQuestions = useCallback(async (page: number) => {
    setLoading(true)
    try {
      const payload = (await loadQuestions(page)).data.payload
      setQuestions(payload.content)
      setTotalPages(payload.totalPages)
    } catch (error) {
      showErrorToast('Sorular yüklenirken hata oluştu')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchQuestions(0)
  }, [fetchQuestions])

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 0 && newPage < totalPages) {
        setCurrentPage(newPage)
        fetchQuestions(newPage)
      }
    },
    [totalPages, fetchQuestions]
  )

  const handleCreateClick = () => {
    setEditingItem(null)
    setShowForm(true)
  }

  const handleEdit = useCallback((question: IQuestionModel) => {
    setEditingItem(question)
    setShowForm(true)
  }, [])

  const handleCloseForm = () => {
    setShowForm(false)
    setEditingItem(null)
  }

  const handleSubmit = async (question: IQuestionModel) => {
    setSaving(true)
    try {
      if (editingItem?.id) {
        await updateQuestion({...question, id: editingItem.id})
        showSuccessToast('Soru başarıyla güncellendi')
        await fetchQuestions(currentPage)
      } else {
        await createQuestion({...question, id: undefined})
        showSuccessToast('Soru başarıyla oluşturuldu')
        setCurrentPage(0)
        await fetchQuestions(0)
      }
      handleCloseForm()
    } catch (error) {
      showErrorToast('Bir hata oluştu')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deletingItem?.id) return
    setLoading(true)
    try {
      await deleteQuestion(deletingItem.id)
      showSuccessToast('Soru başarıyla silindi')
      await fetchQuestions(currentPage)
    } catch (error) {
      showErrorToast('Soru silinirken hata oluştu')
    } finally {
      setDeletingItem(null)
      setLoading(false)
    }
  }

  return (
    <>
      <PageTitle breadcrumbs={[]}>Soru Tanımı</PageTitle>

      <div className='card'>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bolder fs-3 mb-1'>Sorular</span>
            <span className='text-muted mt-1 fw-bold fs-7'>İş başvurularında adaylara sorulan sorular</span>
          </h3>
          <div className='card-toolbar'>
            <button type='button' className='btn btn-sm btn-primary' onClick={handleCreateClick}>
              <i className='bi bi-plus-lg fs-5'></i>
              Yeni Kayıt
            </button>
          </div>
        </div>
        <div className='card-body pt-0'>
          <QuestionTable
            data={questions}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            onEdit={handleEdit}
            onDelete={setDeletingItem}
            loading={loading}
          />
        </div>
      </div>

      <QuestionFormModal
        show={showForm}
        editingItem={editingItem}
        loading={saving}
        onClose={handleCloseForm}
        onSubmit={handleSubmit}
      />

      <ConfirmModal
        show={!!deletingItem}
        header='Silme Onayı'
        text={`"${deletingItem?.label ?? ''}" sorusunu silmek istediğinize emin misiniz?`}
        handleClose={() => setDeletingItem(null)}
        applyOnClick={handleDelete}
      />

      <ToastContainer
        position='top-right'
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
      />
    </>
  )
}

export {QuestionPage}
